import type {
  OpencodeToolPart,
  SessionMessageWithParts,
} from "schemas/generated/opencode";
import type { CoalescedTodos } from "./tool-rows/TodoToolRow";

import React, { useState } from "react";

import {
  getContextualToolTitle,
  isTaskTool,
} from "@agent-ui/features/chat/utils/toolDisplay";
import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import shouldShowV2Debuggers from "@v2/utils/shouldShowV2Debuggers";
import { ChevronDown, Eye, MoreHorizontal, Rocket } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { formatDuration } from "replo-utils/lib/datetime";
import { useInterval } from "replo-utils/react/use-interval";

import { SubagentTaskToolRow } from "./tool-rows/SubagentTaskToolRow";
import { TodoToolRow } from "./tool-rows/TodoToolRow";
import { ToolRow } from "./tool-rows/ToolRow";

export const OpencodeToolSectionCard = ({
  toolParts,
  coalescedTodos,
  isStreaming,
  opencodeSessionUuid,
  opencodeMessageUuid,
  subagentMessages,
}: {
  toolParts: OpencodeToolPart[];
  coalescedTodos: CoalescedTodos | null;
  isStreaming: boolean;
  opencodeSessionUuid: string;
  opencodeMessageUuid: string;
  subagentMessages?: SessionMessageWithParts[];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startTime] = useState(() => (isStreaming ? Date.now() : null));

  const [prevIsStreaming, setPrevIsStreaming] = useState(isStreaming);
  if (prevIsStreaming !== isStreaming) {
    setPrevIsStreaming(isStreaming);
    if (!isStreaming) {
      setIsExpanded(false);
    }
  }

  useInterval(
    () => {
      if (!startTime) {
        return;
      }

      const elapsedTime = Math.floor((Date.now() - startTime) / 1000);
      setElapsedSeconds(elapsedTime);
    },
    isStreaming && startTime ? 1000 : null,
  );

  const hasMoreItems = toolParts.length > 4;
  const visibleToolParts =
    isStreaming && !isExpanded ? toolParts.slice(-4) : toolParts;

  const latestToolPart = toolParts[toolParts.length - 1];
  let headerTitle = "Task Completed";
  if (isStreaming) {
    headerTitle = latestToolPart
      ? `${getContextualToolTitle(
          latestToolPart.tool,
          latestToolPart.state.input,
        )}...`
      : "Starting...";
  }

  const hasDebugMenuItems = shouldShowV2Debuggers();

  const getBackgroundStyle = () => {
    if (isStreaming) {
      return "linear-gradient(white, white) padding-box, var(--color-gradient-ai-30) border-box";
    }

    const bg = isHovering
      ? "linear-gradient(var(--color-sheet), var(--color-sheet))"
      : "linear-gradient(white, white)";

    return `${bg} padding-box, var(--color-border) border-box`;
  };

  return (
    <motion.div
      className={twMerge(
        "relative isolate flex flex-col p-3 rounded-xl w-full gap-3 border-transparent",
        !isStreaming && !isExpanded && "shadow-button-selectable",
        isStreaming ? "border-1" : "border-0.5",
      )}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        background: getBackgroundStyle(),
      }}
      transition={{
        duration: 0.3,
        ease: "easeInOut",
        layout: {
          type: "spring",
          bounce: 0.1,
          duration: 0.3,
        },
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {isStreaming && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-xl"
          style={{ background: "var(--color-gradient-ai-5)" }}
        />
      )}

      <div
        className={twMerge(
          "flex justify-between gap-1.5",
          isStreaming ? "items-center" : "cursor-pointer",
        )}
        onClick={() => {
          if (!isStreaming) {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            {isStreaming ? (
              <Rocket size={14} className="text-gradient-ai" />
            ) : (
              <ChevronDown
                size={14}
                className={twMerge(
                  "transition-transform shrink-0",
                  !isExpanded && "rotate-[-90deg]",
                )}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
              />
            )}
            <div className="flex items-center gap-1.5">
              <span
                className={twMerge(
                  "text-sm line-clamp-1",
                  isStreaming && "text-shimmer",
                )}
              >
                {headerTitle}
              </span>
              {isStreaming && (
                <span className="text-sm whitespace-nowrap text-gradient-ai opacity-75">
                  {formatDuration(elapsedSeconds * 1000)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {isStreaming && toolParts.length > 3 && (
            <Button
              icon
              onClick={() => {
                setIsExpanded(!isExpanded);
              }}
              variant="secondary"
              size="sm"
            >
              <ChevronDown
                className={twMerge(
                  "transition-transform",
                  isExpanded && "rotate-180",
                )}
              />
            </Button>
          )}

          {hasDebugMenuItems && (
            <Menu>
              <MenuTrigger
                render={
                  <Tooltip content="More options" triggerAsChild>
                    <Button icon variant="tertiary" size="sm">
                      <MoreHorizontal />
                    </Button>
                  </Tooltip>
                }
              />
              <MenuContent align="end">
                <MenuItem
                  label="View Message In Admin"
                  startEnhancer={<Eye />}
                  onClick={() => {
                    window.open(
                      `https://admin.replo.app/dashboard/chat-messages?chatMessageId=${opencodeMessageUuid}`,
                      "_blank",
                    );
                  }}
                />
                <MenuItem
                  label="View Session In Admin"
                  startEnhancer={<Eye />}
                  onClick={() => {
                    window.open(
                      `https://admin.replo.app/dashboard/chats?chatId=${opencodeSessionUuid}`,
                      "_blank",
                    );
                  }}
                />
              </MenuContent>
            </Menu>
          )}
        </div>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {(isStreaming || isExpanded) && (
          <motion.div
            key="opencode-tool-rows"
            className="flex flex-col overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            style={
              isStreaming && !isExpanded && hasMoreItems
                ? {
                    WebkitMask:
                      "linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.3) 20%, black 50%)",
                    mask: "linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.3) 20%, black 50%)",
                  }
                : undefined
            }
          >
            {visibleToolParts.map((toolPart, toolIndex) => (
              <motion.div
                key={`${toolPart.callID}-${toolIndex}`}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                {isTaskTool(toolPart.tool) ? (
                  <SubagentTaskToolRow
                    toolPart={toolPart}
                    subagentMessages={subagentMessages ?? []}
                  />
                ) : (
                  <ToolRow toolPart={toolPart} />
                )}
              </motion.div>
            ))}
            {coalescedTodos && coalescedTodos.todos.length > 0 && (
              <motion.div
                key="todo-tool-row"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                <TodoToolRow coalescedTodos={coalescedTodos} />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
